
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { DualHeader } from "@/components/DualHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ArrowLeft, Info, RefreshCcw } from "lucide-react";
import CampingBookingForm from "@/components/camping/components/CampingBookingForm";
import RVBookingForm from "@/components/camping/components/RVBookingForm";
import { getCampingSiteById } from "@/services/campingSite";
import { CampingSite } from "@/services/campingSite/types";
import { Skeleton } from "@/components/ui/skeleton";
import { ClosureNotice } from "@/components/ClosureNotice";
import { CLOSURES, CLOSURE_MESSAGE_RV } from "@/config/closures";
import WildHorseBookingForm from "@/components/camping/components/WildHorseBookingForm";
import { CartItem } from "@/contexts/CartContext";

const BookingPage = () => {
  const {
    siteType,
    siteId
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [siteData, setSiteData] = useState<CampingSite | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editData, setEditData] = useState<CartItem | null>(null);

  useEffect(() => {
    // Check if we have edit data from location state
    if (location.state?.editItem) {
      setEditData(location.state.editItem);
      // Clear location state to prevent re-triggering on navigation
      window.history.replaceState({}, document.title);
    }

    fetchSiteData();
  }, [siteId, location.state]);

  const fetchSiteData = async () => {
    if (!siteId) {
      setError("Invalid site ID");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Fetching site data for ID:', siteId);
      const site = await getCampingSiteById(siteId);
      console.log('Successfully fetched site:', site);
      setSiteData(site);
    } catch (err) {
      console.error('Error fetching site data:', err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      
      if (errorMessage.includes('Site not found')) {
        setError(`Site "${siteId}" not found. Please check the site ID and try again.`);
      } else {
        setError("Failed to load site data. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Determine if it's an RV site based on the site type parameter and site data
  const isRVSite = siteType === 'rv' || (siteData && siteData.siteType === 'rv');

  // Determine if it's a Wild Horse site
  const isWildHorseSite = siteData?.location === 'wild-horse';

  const handleBookingSuccess = () => {
    // Navigate back to the cart page after successful booking
    navigate("/cart");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-tribal-light">
        <DualHeader />
        <div className="max-w-7xl mx-auto py-12 px-4">
          <Button variant="outline" size="sm" className="mb-8" onClick={() => navigate("/permits/camping")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Camping Permits
          </Button>
          
          <Card className="max-w-3xl mx-auto">
            <CardHeader className="border-b pb-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2 mt-2" />
            </CardHeader>
            
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-tribal-light">
        <DualHeader />
        <div className="max-w-7xl mx-auto py-12 px-4">
          <Button variant="outline" size="sm" className="mb-8" onClick={() => navigate("/permits/camping")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Camping Permits
          </Button>
          
          <Card className="max-w-3xl mx-auto">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <Info className="h-12 w-12 text-red-500" />
                </div>
                <h2 className="text-xl font-semibold text-red-600">Unable to Load Site</h2>
                <p className="text-red-500">{error}</p>
                <div className="space-y-2">
                  <Button 
                    onClick={fetchSiteData}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    Try Again
                  </Button>
                  <p className="text-sm text-gray-600">
                    Site ID: {siteId}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!siteData) {
    return (
      <div className="min-h-screen bg-tribal-light">
        <DualHeader />
        <div className="max-w-7xl mx-auto py-12 px-4">
          <Button variant="outline" size="sm" className="mb-8" onClick={() => navigate("/permits/camping")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Camping Permits
          </Button>
          
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-red-500">Site data not available. Please try again.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const locationDisplay = siteData?.location === 'mountain-view' ? 'Mountain View' : siteData?.location === 'sheep-creek' ? 'Sheep Creek' : 'Wild Horse';
  const isPavedSite = siteData?.location === 'wild-horse' && parseInt(siteData.siteNumber.replace('WH-', '').trim(), 10) <= 10;

  // Block direct URL access to sites in temporarily closed areas
  const siteIsClosed = isWildHorseSite ? CLOSURES.wildHorse : CLOSURES.rvSites;
  if (siteIsClosed) {
    return (
      <div className="min-h-screen bg-tribal-light">
        <DualHeader />
        <div className="max-w-3xl mx-auto py-12 px-4">
          <Button variant="outline" size="sm" className="mb-8" onClick={() => navigate("/permits/camping")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Camping Permits
          </Button>
          <ClosureNotice message={CLOSURE_MESSAGE_RV} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tribal-light">
      <DualHeader />
      
      <div className="max-w-7xl mx-auto py-12 px-4">
        <Button variant="outline" size="sm" className="mb-8" onClick={() => navigate("/permits/camping")}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Camping Permits
        </Button>
        
        <Card className="max-w-3xl mx-auto">
          <CardHeader className="border-b pb-4">
            <h1 className="text-2xl font-bold">{editData ? "Edit Booking" : "Book"} {siteData?.siteNumber}</h1>
            <p className="text-tribal-neutral">
              {locationDisplay} - ${siteData?.pricePerNight.toFixed(2)} per night
            </p>
          </CardHeader>
          
          <CardContent className="pt-6">
            {isWildHorseSite && siteData ? (
              <WildHorseBookingForm 
                siteId={siteData.id} 
                siteName={siteData.siteNumber} 
                price={siteData.pricePerNight} 
                isPaved={isPavedSite} 
                onSuccess={handleBookingSuccess} 
                editData={editData}
              />
            ) : isRVSite && siteData ? (
              <RVBookingForm 
                siteId={siteData.id} 
                siteName={siteData.siteNumber} 
                price={siteData.pricePerNight} 
                amenities={{
                  electric: siteData.hasElectricity,
                  water: siteData.hasWater,
                  petsAllowed: (siteData.amenities as any).petsAllowed,
                  amperage: (siteData.amenities as any).amperage,
                  sewage: (siteData.amenities as any).sewage,
                  maxRVLength: (siteData.amenities as any).max_rv_length || (siteData.amenities as any).maxRVLength,
                  wifi: (siteData.amenities as any).wifi
                }} 
                location={locationDisplay} 
                onSuccess={handleBookingSuccess} 
                editData={editData}
              />
            ) : siteData ? (
              <CampingBookingForm 
                siteId={siteData.id} 
                siteName={siteData.siteNumber} 
                price={siteData.pricePerNight} 
                onSuccess={handleBookingSuccess} 
                editData={editData}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingPage;
